<template>
  <div class="signature-zone">
    <label class="signature-zone__subject">{{ $t('signature') }}</label>
    <div class="row">
      <InputField
        class="time-input"
        :showColon="false"
        :value="getSignatureAttribute('time')"
        :label="$t('time')"
        :inputTip="$t('error.invalidTime')"
        inputCheckType="string"
        :pattern="regex.time"
        :showFlag="false"
        :readonly="!editStatus"
        :required="!!signatures.time"
        @updateValue="val=>updateValue(val.trim(),'time')"
      />
    </div>
    <div class="row">
      <label class="label-title large-12">{{ isHandover ? $t('hotb.signatures.takenOverBy') : $t('hotb.signatures.handedOverBy') }}</label>
      <HotbSignatureSection
        ref="headerSignatureRef"
        v-model="headerNames"
        :editStatus="editStatus"
        :signatureMsg="$t('hotb.signatures.tenantSignatory')"
      />
    </div>
    <div class="row">
      <label class="label-title columns small-1 medium-1 large-1">{{ $t('For') }}:</label> <span>{{ tenant }}</span>
    </div>
    <div class="row">
      <div class="small-12 large-8">
        <InputField
          :label="$t('hotb.signatures.presentAddress')"
          :showColon="false"
          :showFlag="false"
          :placeholder="$t('hotb.signatures.placeHolder')"
          :value="getSignatureAttribute('presentAddress')"
          :readonly="!editStatus"
          @updateValue="val=>updateValue(val,'presentAddress')"
        />
      </div>
      <div class="small-12 large-8">
        <InputField
          :label="$t('hotb.signatures.contactPerson')"
          :showColon="false"
          :showFlag="false"
          :value="getSignatureAttribute('contactPerson')"
          :readonly="!editStatus"
          @updateValue="val=>updateValue(val,'contactPerson')"
        />
      </div>
    </div>
    <div class="row">
      <div class="columns column-padding small-12 medium-12 large-6">
        <InputField
          :showColon="false"
          :showFlag="false"
          :label="$t('general.email')"
          :value="getSignatureAttribute('email')"
          :readonly="!editStatus"
          @updateValue="val=>updateValue(val,'email')"
        />
      </div>
      <div class="columns small-12 medium-12 large-2">
        <InputField
          :label="$t('hotb.signatures.telephoneNo')"
          :showColon="false"
          :showFlag="false"
          :value="getSignatureAttribute('teleNo')"
          :readonly="!editStatus"
          @updateValue="val=>updateValue(val,'teleNo')"
        />
      </div>
    </div>
    <div class="row">
      <label class="label-title large-12">{{ isHandover? $t('hotb.signatures.handedOverBy') : $t('hotb.signatures.takenOverBy') }}</label>
      <HotbSignatureSection
        ref="footerSignatureRef"
        v-model="footerNames"
        :editStatus="editStatus"
      />
    </div>
    <div class="row">
      <label class="label-title columns small-1 medium-1 large-1">{{ $t('For') }}:</label>
      <div class="columns">
        <div
          v-for="(item, index) in subsidiary"
          :key="index"
          class="site-subsidiary"
        >
          {{ item }}
        </div>
        <div>{{ $t('hotb.signatures.ownerOfBuilding') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { computed, getCurrentInstance, unref, ref } from 'vue-demi'
import { mapGetters, mapMutations } from 'vuex'
import { getSubsidiaryTextArray } from '@/api/postman/util'
import InputField from '@/controls/InputField'
import formFullnameMixin from '@/pages/formFullnameMixin'
import mutationTypes from '@/store/mutation-types'
import * as regex from '@/utils/regex'
import i18n from '@/utils/i18n'
import { getHotbCompanyName } from './utils'
import HotbSignatureSection from './HotbSignatureSection.vue'

export default {
  components: {
    InputField,
    HotbSignatureSection,
  },
  mixins: [formFullnameMixin],
  props: {
    transactionTime: {
      type: String,
      default: null,
    },
    editStatus: Boolean,
  },
  setup (props) {
    const { proxy } = getCurrentInstance()
    const headerSignatureRef = ref(null)
    const footerSignatureRef = ref(null)
    const emptyValue = computed(() => props.editStatus ? '' : i18n.t('emptyValue'))
    const fallbackNames = ['']
    const headerNames = computed({
      get () {
        const result = lodash.get(proxy.signatures, 'headerNames', fallbackNames) || fallbackNames
        return result.map(item => {
          return item || unref(emptyValue)
        })
      },
      set (newValue) {
        proxy.updateValue(newValue, 'headerNames')
      },
    })
    const footerNames = computed({
      get () {
        const result = lodash.get(proxy.signatures, 'footerNames', fallbackNames) || fallbackNames
        return result.map(item => {
          return item || unref(emptyValue)
        })
      },
      set (newValue) {
        proxy.updateValue(newValue, 'footerNames')
      },
    })
    const getSignatureAttribute = (name) => {
      return lodash.get(proxy.signatures, name) || unref(emptyValue)
    }
    const getImgUrl = () => {
      return {
        headerSignatures: unref(headerSignatureRef).getSignatures(),
        footerSignatures: unref(footerSignatureRef).getSignatures(),
      }
    }
    return {
      headerNames,
      footerNames,
      headerSignatureRef,
      footerSignatureRef,
      getSignatureAttribute,
      getImgUrl,
    }
  },
  data () {
    return {
      regex,
    }
  },
  computed: {
    ...mapGetters('hotb', ['currentHotb', 'isHandover']),
    contacts () {
      return lodash.get(this.currentHotb, 'contacts', [])
        .reduce((partialData, contact) => {
          partialData[contact.type.toLowerCase()] = contact.value
          return partialData
        }, {})
    },
    defaultSignature () {
      const contactPerson = this.getContactPerson()
      return {
        contactPerson,
        email: lodash.get(this.contacts, 'email', ''),
        teleNo: lodash.get(this.contacts, 'phone_number', ''),
        time: this.transactionTime,
      }
    },
    signatures () {
      const currentSignatures = lodash.get(this.currentHotb, 'signatures', {})
      const signatures = Object.assign({}, this.defaultSignature, currentSignatures)
      return signatures
    },
    subsidiary () {
      const result = lodash.get(this.currentHotb, 'physicalSite', {})
      const subsidiary = lodash.get(result, 'subsidiary')
      return getSubsidiaryTextArray(subsidiary)
    },
    tenant () {
      return getHotbCompanyName(this.currentHotb)
    },
  },
  methods: {
    ...mapMutations('hotb', {
      updateHotbByPath: mutationTypes.HOTB_UPDATE_FIELD_PATH,
    }),
    getContactPerson () {
      const firstName = lodash.get(this.contacts, 'name.firstName', '')
      const lastName = lodash.get(this.contacts, 'name.lastName', '')
      if (firstName || lastName) {
        return this.displayI18nFullName(firstName, lastName)
      }
      return ''
    },
    updateValue (newValue, field) {
      this.updateHotbByPath({
        path: ['signatures', field],
        newValue,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.signature-zone {
  display: inline;
  &__subject {
    font-size: 16px;
    font-weight: bold;
  }
  & ::v-deep .text-input {
    textarea,input{
      width:100% !important;
    }
  }
  .label-title {
    margin-top: 15px;
    margin-bottom: 5px;
    font-size: 14px;
    line-height: 1.43;
    font-weight: bold;
    text-align: left;
    color: #0C3A58;
    &.columns {
      margin: 0
    }
  }

  .column-padding {
    padding-right: 10px;
  }
  .time-input {
    width: 100px;
  }
  .site-subsidiary {
    font-style: italic;
    transform: scale(0.71);
    transform-origin: left center;
    &:first-child {
      transform: scale(1);
      font-size: inherit;
      font-style: inherit;
      letter-spacing: inherit;
    }
  }
}
</style>
